<template>
  <div
    v-show="expandedLayoutId === null || expandedLayoutId === layout.id"
    class="border border-gray-200 rounded-lg overflow-hidden"
  >
    <!-- Layout Preview Header -->
    <div class="bg-gray-50 p-4 flex justify-between items-center">
      <div class="flex items-center gap-4">
        <div class="flex flex-col text-left">
          <span class="text-gray-700 font-inter text-lg font-normal">
            Name: {{ layout.layoutName }}
          </span>
          <span class="text-gray-700 font-inter text-lg font-normal">
            Type: {{ layout.layoutType }} | Views: {{ layout.viewCount }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <BaseButton
          type="button"
          class="text-primary-purple font-inter text-sm font-medium"
          @click="() => $emit('showCode', layout.uuid)"
        >
          Setup Code
        </BaseButton>
        <BaseButton
          type="button"
          class="text-primary-purple font-inter text-sm font-medium"
          @click="$emit('toggleExpanded', layout.id)"
        >
          {{ expandedLayoutId === layout.id ? "Collapse" : "Edit" }}
        </BaseButton>
        <BaseButton
          type="button"
          class="text-red-700 font-inter text-sm font-medium ml-2"
          @click="$emit('delete', layout.id)"
        >
          Delete
        </BaseButton>
      </div>
    </div>

    <!-- Layout Content with Preview and Controls -->
    <div class="flex gap-1">
      <!-- Layout Preview -->
      <section
        class="flex flex-col max-md:ml-0 max-md:w-full"
        :class="{
          'w-full': expandedLayoutId !== layout.id,
          'w-[70%]': expandedLayoutId === layout.id,
        }"
      >
        <DirectoryPreview
          :layout="layout"
          :profiles="profiles"
          :approvedExperts="approvedExperts"
          :userStore="userStore"
          :expandedLayoutId="expandedLayoutId"
        />
      </section>

      <!-- Styling Controls (Right Side) -->
      <section
        v-if="expandedLayoutId === layout.id"
        class="flex flex-col ml-5 w-[30%] max-md:ml-0 max-md:w-full"
      >
        <DirectoryControls
          :layout="layout"
          :approvedExperts="approvedExperts"
          :fieldErrors="fieldErrors"
          :updateDirectoryLoading="updateDirectoryLoading"
          :userStore="userStore"
          @update="(updatedLayout: DirectoryLayout) => $emit('update', updatedLayout)"
          @cancel="$emit('cancelEdit')"
          @updateLayoutName="(id: string, name: string) => $emit('updateLayoutName', id, name)"
          @updateLayoutType="(id: string, type: string) => $emit('updateLayoutType', id, type)"
          @updateLayoutStyle="
            (id: string, style: string) => $emit('updateLayoutStyle', id, style)
          "
          @updateLayoutCosmetic="
            (id: string, property: string, value: string | number | boolean) =>
              $emit('updateLayoutCosmetic', id, property, value)
          "
          @updateLayoutHeader="
            (id: string, property: string, value: string | number | boolean) =>
              $emit('updateLayoutHeader', id, property, value)
          "
          @updateLayoutWebsite="
            (id: string, url: string) => $emit('updateLayoutWebsite', id, url)
          "
          @updateLayoutSearchFilters="
            (id: string, enabled: boolean) => $emit('updateLayoutSearchFilters', id, enabled)
          "
          @updateLayoutSelectedExperts="
            (id: string, ids: string[], fieldIndex?: number) =>
              $emit('updateLayoutSelectedExperts', id, ids, fieldIndex)
          "
          @updateLayoutRandomExperts="
            (id: string, isRandom: boolean) => $emit('updateLayoutRandomExperts', id, isRandom)
          "
        />
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import DirectoryPreview from "./DirectoryPreview.vue";
import DirectoryControls from "./DirectoryControls.vue";
import {
  DirectoryLayout,
  ApprovedExpert,
  Profile,
} from "@/types/dashboard-types";

export default defineComponent({
  name: "DirectoryLayoutCard",
  components: {
    BaseButton,
    DirectoryPreview,
    DirectoryControls,
  },
  props: {
    layout: {
      type: Object as () => DirectoryLayout,
      required: true,
    },
    expandedLayoutId: {
      type: String as () => string | null,
      default: null,
    },
    profiles: {
      type: Array as () => Profile[],
      required: true,
    },
    approvedExperts: {
      type: Array as () => ApprovedExpert[],
      required: true,
    },
    userStore: {
      type: Object,
      required: true,
    },
    fieldErrors: {
      type: Object as () => Record<string, string>,
      required: true,
    },
    updateDirectoryLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "showCode",
    "toggleExpanded",
    "delete",
    "update",
    "cancelEdit",
    "updateLayoutName",
    "updateLayoutType",
    "updateLayoutStyle",
    "updateLayoutCosmetic",
    "updateLayoutHeader",
    "updateLayoutWebsite",
    "updateLayoutSearchFilters",
    "updateLayoutSelectedExperts",
    "updateLayoutRandomExperts",
  ],
});
</script>
